import { useState, useEffect, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUserTie,
  faBoxes,
  faBox,
  faCheckCircle,
  faPhone,
  faEnvelope,
  faEdit,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const getSellerId = (seller) =>
  String(seller?.shop_saller_id || seller?.shopSellerId || seller?.id || "");

const normalizeSeller = (seller) => {
  if (!seller) return null;
  return {
    id: getSellerId(seller),
    regNo: seller.username || seller.regNo || "N/A",
    name: seller.user?.full_name || seller.name || seller.username || "Unknown",
    phone: seller.user?.phone || seller.phone || "N/A",
    email: seller.user?.email || seller.email || "N/A",
  };
};

const normalizeStock = (stock) => {
  if (!stock) return null;
  const product = stock.product || {};
  const category = product.product_category || product.category || {};
  const type = category.product_type || product.product_type || {};
  const dozenAmount = Number(stock.dozen_amount ?? stock.dozenAmount ?? 0);
  const noOfItems = Number(product.no_of_items ?? stock.noOfItems ?? 1);
  const totalItems = Number(
    stock.summary?.totalItems ?? stock.totalItems ?? dozenAmount * noOfItems,
  );
  const remainingItems = Number(
    stock.summary?.remainingItems ??
      stock.remainingItems ??
      stock.remaining_items ??
      totalItems,
  );

  return {
    stockId: stock.stockId || stock.id,
    productName: product.product_name || stock.productName || "Unknown product",
    productType:
      (typeof type === "string" ? type : type.name) || stock.productType || "",
    productCategory: category.category_name || stock.productCategory || "",
    packageType: product.package_type || stock.packageType || "",
    noOfItems,
    dozenAmount,
    totalItems,
    remainingItems,
    purchasePrice: parseFloat(stock.purchase_price ?? stock.purchasePrice) || 0,
    sellingPrice: parseFloat(stock.selling_price ?? stock.sellingPrice) || 0,
    purchaseDate: stock.purchase_date || stock.purchaseDate,
    shopId: stock.shopId || stock.shop_id,
    shopName: stock.shop?.shop_name || stock.shopName || "",
  };
};

const EditSaleModal = ({ sale, onUpdate, onClose, sellers, stockData }) => {
  const [form, setForm] = useState({
    sellerId: "",
    stockId: "",
    sellingPrice: "",
    noOfUnSold: "",
    status: "active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedSellers = useMemo(
    () => (Array.isArray(sellers) ? sellers.map(normalizeSeller) : []),
    [sellers],
  );

  const normalizedStocks = useMemo(
    () => (Array.isArray(stockData) ? stockData.map(normalizeStock) : []),
    [stockData],
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sw-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  useEffect(() => {
    if (!sale) return;

    const sellerMatch = normalizedSellers.find(
      (s) =>
        s.id === String(sale.sellerId || "") ||
        (sale.sellerRegNo && s.regNo === sale.sellerRegNo),
    );

    setForm({
      sellerId: sellerMatch?.id || String(sale.sellerId || ""),
      stockId: sale.stockId || "",
      sellingPrice:
        sale.actualPricePerItem !== undefined && sale.actualPricePerItem !== null
          ? String(sale.actualPricePerItem)
          : "",
      noOfUnSold: (sale.noOfUnSold ?? sale.no_of_unsold)?.toString() || "",
      status: sale.status || "active",
    });

    const stock = normalizedStocks.find((s) => s.stockId === sale.stockId);
    setSelectedStock(stock || null);
    setSelectedSeller(sellerMatch || null);
    setIsSubmitting(false);
  }, [sale, normalizedStocks, normalizedSellers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleSubmit = () => {
    const errors = {};

    if (!form.sellerId) {
      errors.sellerId = "Please select a seller";
    }
    if (!form.stockId) {
      errors.stockId = "Please select a product";
    }
    if (!form.sellingPrice || parseFloat(form.sellingPrice) <= 0) {
      errors.sellingPrice = "Please enter a valid selling price per item";
    }
    if (!form.noOfUnSold || parseInt(form.noOfUnSold) < 0) {
      errors.noOfUnSold = "Please enter valid number of unsold items";
    }

    if (selectedStock && parseInt(form.noOfUnSold) > selectedStock.totalItems) {
      errors.noOfUnSold = `Cannot exceed total items (${selectedStock.totalItems})`;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    const seller = normalizedSellers.find((s) => s.id === form.sellerId);
    const stock = normalizedStocks.find((s) => s.stockId === form.stockId);

    try {
      const result = onUpdate({
        salesId: sale.salesId,
        sellerId: seller?.id,
        sellerRegNo: seller?.regNo,
        sellerName: seller?.name,
        sellerEmail: seller?.email,
        sellerPhone: seller?.phone,
        stockId: stock?.stockId,
        productName: stock?.productName || sale.productName,
        productType: stock?.productType || sale.productType,
        productCategory: stock?.productCategory || sale.productCategory,
        packageType: stock?.packageType || sale.packageType,
        noOfItems: stock?.noOfItems,
        dozenAmount: stock?.dozenAmount,
        totalItems: stock?.totalItems,
        purchasePrice: stock?.purchasePrice,
        selling_price: parseFloat(form.sellingPrice),
        purchaseDate: stock?.purchaseDate,
        no_of_unsold: parseInt(form.noOfUnSold),
        status: form.status,
        shopId: stock?.shopId,
        shopName: stock?.shopName,
      });

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess(
              `Sale for "${stock?.productName || sale.productName}" updated successfully!`,
            );
            setIsSubmitting(false);
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to update sale: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess(
          `Sale for "${stock?.productName || sale.productName}" updated successfully!`,
        );
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to update sale: ${error.message || "Please try again."}`,
      );
      setIsSubmitting(false);
    }
  };

  if (!sale) {
    return null;
  }

  const displayProductType = selectedStock?.productType || sale.productType || "-";
  const displayProductCategory =
    selectedStock?.productCategory || sale.productCategory || "-";
  const displayPackageType =
    selectedStock?.packageType || sale.packageType || "-";
  const displayTotalItems =
    selectedStock?.totalItems || sale.totalItemsInStock || 0;
  const displayPurchasePrice =
    selectedStock?.purchasePrice || parseFloat(sale.purchasePrice) || 0;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Sale"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update Sale
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">Edit Sale</p>
            <p className="text-sm text-gray-600 font-medium">
              Update sale information
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUserTie}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Seller<span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.sellerId}
            onValueChange={(value) => {
              setForm({ ...form, sellerId: value });
              const seller = normalizedSellers.find((s) => s.id === value);
              setSelectedSeller(seller || null);
              if (formErrors.sellerId) {
                setFormErrors({ ...formErrors, sellerId: "" });
              }
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${formErrors.sellerId ? "border-red-500" : "border-gray-300"}`}
            >
              <SelectValue placeholder="Select seller" />
            </SelectTrigger>
            <SelectContent>
              {normalizedSellers.map((seller) => (
                <SelectItem key={seller.id} value={seller.id}>
                  {seller.regNo} - {seller.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.sellerId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.sellerId}
            </p>
          )}
          {(selectedSeller || sale.sellerName) && (
            <div className="text-xs text-gray-600 flex items-center gap-2 mt-1">
              <FontAwesomeIcon icon={faPhone} className="h-3 w-3" />
              <span className="text-blue-800 font-bold">
                {selectedSeller?.phone || sale.sellerPhone || "N/A"}
              </span>
              <FontAwesomeIcon icon={faEnvelope} className="h-3 w-3 ml-2" />
              <span className="text-orange-600 font-bold">
                {selectedSeller?.email || sale.sellerEmail || "N/A"}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBoxes}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product <span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.stockId}
            onValueChange={(value) => {
              setForm({ ...form, stockId: value });
              const stock = normalizedStocks.find((s) => s.stockId === value);
              setSelectedStock(stock || null);
              if (formErrors.stockId) {
                setFormErrors({ ...formErrors, stockId: "" });
              }
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${formErrors.stockId ? "border-red-500" : "border-gray-300"}`}
            >
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {normalizedStocks.map((stock) => (
                <SelectItem key={stock.stockId} value={stock.stockId}>
                  {stock.productName} ({stock.totalItems} items)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.stockId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.stockId}
            </p>
          )}
        </div>

        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-gray-600">Product Type</p>
              <p className="font-medium text-gray-900">{displayProductType}</p>
            </div>
            <div>
              <p className="text-gray-600">Category</p>
              <p className="font-medium text-gray-900">
                {displayProductCategory}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Package Type</p>
              <p className="font-medium text-gray-900">{displayPackageType}</p>
            </div>
            <div>
              <p className="text-gray-600">Total Items</p>
              <p className="font-medium text-blue-600">
                {displayTotalItems} units
              </p>
            </div>
            <div>
              <p className="text-gray-600">Purchase Price</p>
              <p className="font-medium text-gray-900">
                {formatCurrency(displayPurchasePrice)}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Selling Price</p>
              <p className="font-medium text-green-600">
                {formatCurrency(
                  selectedStock?.sellingPrice ||
                    parseFloat(sale.actualSellingPrice) ||
                    0,
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Selling Price (Per Item)<span className="text-red-700">*</span>
          </Label>
          <Input
            name="sellingPrice"
            type="number"
            min="0"
            step="0.01"
            value={form.sellingPrice}
            onChange={handleChange}
            placeholder="Enter selling price per item"
            className={`h-10 text-sm ${formErrors.sellingPrice ? "border-red-500" : "border-gray-300"}`}
          />
          {formErrors.sellingPrice && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.sellingPrice}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBox}
              className="text-blue-800 h-3.5 w-3.5"
            />
            No of Unsold Items <span className="text-red-700">*</span>
          </Label>
          <Input
            name="noOfUnSold"
            type="number"
            min="0"
            value={form.noOfUnSold}
            onChange={handleChange}
            placeholder="Enter number of unsold items"
            className={`h-10 text-sm ${formErrors.noOfUnSold ? "border-red-500" : "border-gray-300"}`}
          />
          {formErrors.noOfUnSold && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.noOfUnSold}
            </p>
          )}
          {form.noOfUnSold !== "" && (
            <div className="text-xs text-gray-600 mt-1">
              Items Sold:{" "}
              {Math.max(
                displayTotalItems - parseInt(form.noOfUnSold || 0),
                0,
              )}
              {parseFloat(form.sellingPrice) > 0 && (
                <span className="ml-2 text-green-600 font-semibold">
                  Total Revenue:{" "}
                  {formatCurrency(
                    parseFloat(form.sellingPrice) *
                      Math.max(
                        displayTotalItems - parseInt(form.noOfUnSold || 0),
                        0,
                      ),
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Status
          </Label>
          <Select
            value={form.status}
            onValueChange={(value) => setForm({ ...form, status: value })}
          >
            <SelectTrigger className="h-10 text-sm border-gray-300">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  );
};

export default EditSaleModal;